import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '@/components/Header';
import ProgressIndicator from '@/components/ProgressIndicator';
import Step1MeasurementType from '@/components/steps/Step1MeasurementType';
import Step2BlendAreas from '@/components/steps/Step2BlendAreas';
import Step3Wastage from '@/components/steps/Step3Wastage';
import Step4Mesh from '@/components/steps/Step4Mesh';
import Step5Ancillaries from '@/components/steps/Step5Ancillaries';
import Step6CustomerDetails from '@/components/steps/Step6CustomerDetails';
import Step7Summary from '@/components/steps/Step7Summary';
import { useQuoteStore } from '@/store/quoteStore';
import API_URL from '@/config/api';
import logger from '@/utils/logger';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { SURFACING_TO_SPEC } from '@/data/probound';

const QuoteCalculator = () => {
  const { currentStep, setCurrentStep, setBlendAreas, setMeasurementType, setCustomerDetails, setWastage, setRequiresMesh, reset } = useQuoteStore();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isEmbedMode = searchParams.get('embed') === 'true';
  const leadId = searchParams.get('lead');
  const projectId = searchParams.get('project');
  const sourceQuoteId = searchParams.get('quote'); // customer quote → "Get material price"

  // Fresh-start guard: when the builder is opened without a project/lead pre-fill
  // (i.e. the user tapped "Build a new order"), wipe any stale Zustand state left
  // behind from a previously submitted quote so they land on Step 1 with a clean slate.
  // Runs once on mount.
  useEffect(() => {
    if (!projectId && !leadId && !sourceQuoteId) reset();
  }, [projectId, leadId, sourceQuoteId, reset]);

  // Pre-fill straight from a customer quote (/order?quote=<id>): areas → blend + m² + spec,
  // customer details, then jump to the summary so the installer sees the material price immediately.
  useEffect(() => {
    if (!sourceQuoteId || projectId) return;
    reset();
    (async () => {
      try {
        const qRes = await fetch(`${API_URL}/api/project-quotes/${sourceQuoteId}`, { credentials: 'include' });
        if (!qRes.ok) return;
        const q = await qRes.json();
        setCustomerDetails({
          name: q.installer_name || user?.name || '', email: user?.email || '', phone: q.installer_phone || user?.phone || '',
          customer_name: q.customer_name || '', address: q.customer_address || '', start_date: '',
        });
        const items = (q.quote_data?.surfacing_items || []).filter((s) => (s.squareMetres || s.square_metres || 0) > 0);
        if (!items.length) return;
        setMeasurementType('Square metres (m²)');
        setWastage('10%');
        setRequiresMesh(false);
        setBlendAreas(items.map((s) => ({
          surface_blend: s.blend || '',
          quantity: String(s.squareMetres || s.square_metres || ''),
          specification: SURFACING_TO_SPEC[s.id] || 'Vehicle traffic - surface only',
          base_mixes: '',
        })));
        const complete = items.every((s) => s.blend);
        if (!complete) toast.message('Pick a blend colour for each area, then continue to see your material price.');
        setCurrentStep(complete ? 7 : 2);
      } catch (e) {
        logger.error('Failed to prefill order from customer quote:', e);
      }
    })();
  }, [sourceQuoteId, projectId, user?.email, reset, setCustomerDetails, setMeasurementType, setBlendAreas, setCurrentStep, setWastage, setRequiresMesh]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill from project's source quote when launched as /order?project=<id>
  useEffect(() => {
    if (!projectId) return;
    // Clear any stale state from a previous quote before applying the project prefill
    reset();
    (async () => {
      try {
        const projRes = await fetch(`${API_URL}/api/projects/${projectId}`, { credentials: 'include' });
        if (!projRes.ok) return;
        const project = await projRes.json();
        setCustomerDetails({
          name: '', email: '', phone: '',
          customer_name: project.customer_name || '',
          address: project.customer_address || '',
          start_date: project.install_date || '',
        });
        const sourceQuoteId = project.source_quote_id;
        if (!sourceQuoteId) return;
        const qRes = await fetch(`${API_URL}/api/project-quotes/${sourceQuoteId}`, { credentials: 'include' });
        if (!qRes.ok) return;
        const q = await qRes.json();
        const items = (q.quote_data?.surfacing_items || []).filter((s) => (s.squareMetres || s.square_metres || 0) > 0);
        if (items.length) {
          setMeasurementType('Square metres (m²)');
          setBlendAreas(items.map((s) => ({
            surface_blend: s.name || s.surface_blend || '',
            quantity: String(s.squareMetres || s.square_metres || ''),
            specification: 'Vehicle traffic - surface only',
            base_mixes: '',
          })));
        }
      } catch (e) {
        logger.error('Failed to prefill order from project:', e);
      }
    })();
  }, [projectId, reset, setCustomerDetails, setMeasurementType, setBlendAreas]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1MeasurementType />;
      case 2:
        return <Step2BlendAreas />;
      case 3:
        return <Step3Wastage />;
      case 4:
        return <Step4Mesh />;
      case 5:
        return <Step5Ancillaries />;
      case 6:
        return <Step6CustomerDetails />;
      case 7:
        return <Step7Summary leadId={leadId} projectId={projectId} sourceQuoteId={sourceQuoteId} />;
      default:
        return <Step1MeasurementType />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {!isEmbedMode && <Header />}
      <div className="flex-1 flex flex-col items-center p-4 md:p-8">
        <div className="w-full max-w-2xl">
          {/* Section Title */}
          <div className="flex justify-center mb-4">
            <h1 className="text-2xl md:text-3xl font-bold text-[#27292E]">Quote Builder</h1>
          </div>
          <ProgressIndicator />
          <div className="step-transition mt-8">
            {renderStep()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteCalculator;